import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PartnerService } from '../../services/partnerService';
import { Package, DollarSign, TrendingUp, Calendar, Search } from 'lucide-react';

const PartnerOrders = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['partnerOrders', user?.id],
    queryFn: () => PartnerService.getOrders(user?.id),
    enabled: !!user?.id,
  });
  const orders = ordersData?.data || [];

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status?.toLowerCase() === filterStatus.toLowerCase());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">Manage your orders</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'all' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'active' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'completed' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-slate-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No orders found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">#{order.id?.slice(0, 8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.service_type || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">₹{Number(order.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      order.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {order.status || 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PartnerRevenue = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  const { data: revenueData, isLoading } = useQuery({
    queryKey: ['partnerRevenue', user?.id, dateRange],
    queryFn: () => PartnerService.getRevenue(user?.id, dateRange),
    enabled: !!user?.id,
  });
  const revenue = revenueData?.data || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Revenue</h1>
          <p className="text-gray-600 mt-1">Track your revenue and earnings</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="End Date"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <DollarSign size={20} className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            ₹{Number(revenue.totalRevenue || 0).toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Orders Completed</p>
            <Package size={20} className="text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{revenue.ordersCompleted || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Average Order Value</p>
            <TrendingUp size={20} className="text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            ₹{Number(revenue.averageOrderValue || 0).toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Revenue Chart</h2>
        <div className="h-64 flex items-center justify-center text-gray-500">
          <p>Revenue chart will be displayed here</p>
        </div>
      </div>
    </div>
  );
};

export { PartnerOrders, PartnerRevenue };
